import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useParams } from "react-router-dom";

import { CompositionSourceGroup, unwrapSmartTableCollectionPayload } from "./types";

import { ENDPOINTS } from "@/api/application-api";
import { useAuth } from "@/context/auth-context";
import { handleResponse } from "@/utils/errorResponseHandlers";
import { jsonAuthHeaders } from "@/utils/headers";

export interface UseListAvailableSourcesOptions {
  /**
   * When provided, sent to the server as ``exclude_sheet_id``. Typically the
   * current sheet, to avoid surfacing a trivial self-reference.
   */
  excludeSheetId?: string;
  /**
   * Sent to the server as ``output_only``. Server default is ``true``; this
   * hook defaults to ``false`` for the first-pass Composition flow.
   */
  outputOnly?: boolean;
  /**
   * Client-side filter restricting returned groups to a single smart table.
   * The discovery endpoint itself does not support this filter; scoping
   * happens on the client so the cache stays workspace-wide.
   */
  smartTableId?: string;
  /**
   * Override for ``useQuery.enabled``. Defaults to the standard
   * ``!!workspaceId`` guard.
   */
  enabled?: boolean;
}

/**
 * Discover composable columns across every sheet/table in the current
 * workspace.
 *
 * Server contract:
 *   - URL: ``GET /api/dashboard/v2/workspaces/<wid>/smart-tables/columns/available-sources``
 *   - Query args: ``exclude_sheet_id?: UUID``, ``output_only?: bool``
 *   - The server default for ``output_only`` is ``true``. This hook
 *     **always** passes ``false`` so the first-pass Composition flow sees
 *     every column regardless of the deferred FR-15 output-column toggle.
 *   - The response payload is ``{ success, sources: CompositionSourceGroup[] }``.
 *
 * Client-side filtering:
 *   - ``smartTableId`` is applied in-memory via ``.filter`` on the returned
 *     groups, so the React Query cache remains keyed only by arguments the
 *     server sees. This keeps two consumers with different ``smartTableId``
 *     values from thrashing the cache against each other.
 */
export const useListAvailableSources = ({
  excludeSheetId,
  outputOnly = false,
  smartTableId,
  enabled,
}: UseListAvailableSourcesOptions = {}) => {
  const userToken = useAuth()?.userToken ?? "";
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const wid = Number(workspaceId);

  const query = useQuery<CompositionSourceGroup[]>({
    // Cache on the server-visible inputs only; `smartTableId` is a pure
    // client-side filter so it intentionally does not appear in the key.
    queryKey: ["smart-tables", wid, "available-sources", { excludeSheetId, outputOnly }],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (excludeSheetId) {
        params.set("exclude_sheet_id", excludeSheetId);
      }
      params.set("output_only", outputOnly ? "true" : "false");

      const baseUrl = ENDPOINTS.smart_table_available_sources(wid);
      const url = params.toString() ? `${baseUrl}?${params.toString()}` : baseUrl;

      const response = await fetch(url, { headers: jsonAuthHeaders(userToken) });
      const payload = await handleResponse(response);
      return unwrapSmartTableCollectionPayload<CompositionSourceGroup>(payload, "sources");
    },
    enabled: enabled ?? (!!workspaceId && !Number.isNaN(wid)),
  });

  const filteredGroups = useMemo(() => {
    const groups = query.data ?? [];
    if (!smartTableId) {
      return groups;
    }
    return groups.filter(group => group.smart_table_id === smartTableId);
  }, [query.data, smartTableId]);

  return {
    ...query,
    data: filteredGroups,
  };
};
